import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import CheckIcon from '@material-ui/icons/Check';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import RoomIcon from '@material-ui/icons/Room';
import CustomerCard from '../components/CustomerCard';
import { roundToTwoDecimals } from '../helper';
import { apiGet } from '../brainApi';
import { DispatchContext } from '../Context';

const useStyles = makeStyles(() => ({
  serviceWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    overflow: 'auto',
  },
  fullWidth: {
    width: '100%',
  },
  serviceCard: {
    display: 'flex',
    width: '100%',
    background: '#ffffff',
  },
  listDivider: {
    margin: '10px 0',
  },
  listIndicator: {
    background: '#cecece',
    width: 4,
    height: 4,
    borderRadius: '50%',
    marginRight: 8,
    marginTop: 7,
    marginLeft: 8,
    display: 'table',
  },
  displayList: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: 8,
    fontSize: 12,
    color: '#959393',
  },
  countCircle: {
    width: 10,
    height: 10,
    padding: 3,
    borderRadius: '50%',
    color: '#fff',
    fontSize: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 'inherit',
  },
  countDivider: {
    backgroundColor: 'rgba(0, 0, 0, 0.26)',
    width: 1,
    height: '100%',
    marginBottom: 5,
    marginTop: 10,
  },
  listlabel: {
    color: '#c1c1c1',
  },
}));

const DispatchVehicleTimeSlots = ({ data, date }) => {
  const { mapData, setMapData } = useContext(DispatchContext);

  const { service_vehicle_id, service_vehicle_name, schedule_instance_id } =
    data || {};
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [
    selectedScheduleAppointments,
    setSelectedScheduleAppointments,
  ] = useState(null);
  const [serviceVehicleAddress, setServiceVehicleAddress] = useState(null);

  const addMapData = (routeData) => {
    const { stops = [], total_minutes = 0 } = routeData;

    if (
      !mapData?.find((datum) => datum.service_vehicle_id === service_vehicle_id)
    ) {
      setMapData((prev) => {
        if (prev) {
          return [
            ...prev,
            {
              stops,
              service_vehicle_id,
              selected: true,
              totalMinutes: total_minutes,
            },
          ];
        }
        return [
          {
            stops,
            service_vehicle_id,
            selected: true,
            totalMinutes: total_minutes,
          },
        ];
      });
    }
  };

  useEffect(() => {
    setLoading(true);
    const dateCopy = moment.parseZone(date).format('YYYY-MM-DD');
    const params = { start_date: dateCopy, end_date: dateCopy };
    apiGet('/get-dispatch-info', params).then((json) => {
      setScheduleData(json?.schedule);
      const selectedScheduleData = json?.schedule.find(
        (s) => s?.schedule_instance_id === schedule_instance_id,
      );
      if (selectedScheduleData) {
        setSelectedScheduleAppointments(selectedScheduleData.appointments);
        setServiceVehicleAddress(selectedScheduleData.service_vehicle_address);
      } else {
        setSelectedScheduleAppointments(null);
      }
      setLoading(false);
    });
  }, [date]);

  useEffect(() => {
    const selectedScheduleData = scheduleData?.find(
      (s) => s?.schedule_instance_id === data?.schedule_instance_id,
    );
    if (selectedScheduleData) {
      setSelectedScheduleAppointments(selectedScheduleData.appointments);
      setServiceVehicleAddress(selectedScheduleData.service_vehicle_address);
      addMapData(selectedScheduleData?.route_data);
    } else {
      setSelectedScheduleAppointments(null);
      setServiceVehicleAddress(null);
    }
  }, [data, scheduleData]);

  if (loading) {
    return 'Loading...';
  }

  const renderServiceVehicleAddress = (address = serviceVehicleAddress) => (
    <Box display="flex" ml={0.5} minHeight={50}>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        mr={0.6}
        mt={0.5}
        color="rgba(0, 0, 0, 0.26)"
      >
        <RoomIcon />
      </Box>
      <Box mr={2} mt={1} mb={1} display="flex">
        <Box variant="body2" fontSize={12} className={classes.listlabel}>
          {address}
        </Box>
      </Box>
    </Box>
  );

  if (mapData?.filter((data) => data.selected).length > 1) {
    const initialAddresses = mapData
      .map((data) => data.stops?.[0]?.address)
      .filter((a) => a);
    return initialAddresses.map((a) => renderServiceVehicleAddress(a));
  }

  return (
    <>
      <Box className="serviceAccordion">
        <Box className={classes.serviceWrapper}>
          <Box className={classes.fullWidth}>
            <Typography variant="h6">{service_vehicle_name}</Typography>
            {!!serviceVehicleAddress && renderServiceVehicleAddress()}
            {selectedScheduleAppointments?.map((sw, index) => (
              <TimeSlotMapAppointment sw={sw} index={index} />
            ))}
            {!!serviceVehicleAddress && renderServiceVehicleAddress()}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DispatchVehicleTimeSlots;

const TimeSlotMapAppointment = ({ sw, index }) => {
  const swIsFinished = sw.status === 'finished';
  const classes = useStyles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [isExpanded, setIsExpanded] = useState(!swIsFinished);

  const {
    scheduled_work_id,
    vehicle,
    services = [],
    total_labor_hours,
    confirmed_appointment_start_time,
  } = sw || {};
  const {
    display_name: vehicleDisplayName,
    license_plate_number,
    license_plate_state,
  } = vehicle || {};

  const serviceNames = services.reduce((acc, next) => {
    return `${acc}${acc.length ? ', ' : ''} ${next.display_name}`;
  }, '');

  const totalLaborHours = Number.isInteger(total_labor_hours)
    ? total_labor_hours
    : total_labor_hours.toFixed(1);

  if (!sw?.scheduled_work_id) {
    return 'No data available';
  }

  return (
    <Box
      className={classes.serviceCard}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <Box className={`${classes.fullWidth}`}>
        <Box display="flex" ml={1}>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            mr={0.6}
            mt={0.5}
          >
            <span
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.26)' }}
              className={classes.countCircle}
            >
              {index + 1}
            </span>
            <span className={classes.countDivider} />
          </Box>
          <Box
            mt={1}
            mb={3}
            display="flex"
            mx={1}
            width="100%"
            style={{
              backgroundColor: swIsFinished ? '#f3f3f3' : 'white',
              borderRadius: '5px',
              boxShadow: '0px 1px 3px #a5a3a3',
              padding: 10,
            }}
          >
            <Box width={isSmall ? 'max-content' : '100%'}>
              <Box
                display="flex"
                alignItems="flex-start"
                flexDirection={isSmall ? 'column' : 'row'}
              >
                <Box width="100%" pr={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {sw.customer && <CustomerCard record={sw} storderId={sw.st_order_id}/>}
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      flexDirection="column"
                    >
                      <Box color="#959393">
                        {moment(
                          confirmed_appointment_start_time,
                          'HH:mm:ss',
                        ).format('h:mm a')}
                      </Box>
                    </Box>
                    {swIsFinished && (
                      <CheckIcon
                        color="action"
                        style={{ display: 'inline', color: 'green' }}
                      />
                    )}
                  </Box>
                  <Box className={classes.displayList}>
                    {sw.address?.one_line_human_readable_address}
                  </Box>
                  <Collapse in={isExpanded}>
                    <Divider className={classes.listDivider} />
                    {vehicleDisplayName && (
                      <Box className={classes.displayList}>
                        <span className={classes.listIndicator} />
                        {vehicleDisplayName}
                      </Box>
                    )}
                    {license_plate_state && (
                      <Box className={classes.displayList}>
                        <span className={classes.listIndicator} />
                        {`${license_plate_number} ${license_plate_state}`}
                      </Box>
                    )}
                    <Box className={classes.displayList}>
                      <span className={classes.listIndicator} />
                      {`${roundToTwoDecimals(
                        totalLaborHours,
                      )} hrs - ${serviceNames}`}
                    </Box>
                    <Box mt={1} fontSize={12}>
                      <Link
                        to={`/scheduled-work/${scheduled_work_id}/show`}
                      >
                        Manage Appointment
                      </Link>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
